import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Barbell, Lock, ArrowClockwise, CheckCircle, Circle, Flame, Play } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function getWeekStart() {
  const d = new Date();
  const day = d.getDay(); // 0 = Sun
  const diff = (day + 6) % 7; // days since Monday
  d.setDate(d.getDate() - diff);
  return d.toISOString().slice(0, 10);
}

export default function GymPlan() {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [isSample, setIsSample] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [completed, setCompleted] = useState(new Set());
  const [matchStatus, setMatchStatus] = useState(null);
  const [videoModal, setVideoModal] = useState(null); // {name, video_id, search_url}
  const weekStart = getWeekStart();

  const load = async () => {
    try {
      const { data } = await api.get("/plans/gym");
      setPlan(data.content);
      setIsSample(data.is_sample);
    } catch (e) { toast.error("Could not load plan"); }
  };

  const loadSessions = async () => {
    try {
      const { data } = await api.get(`/sessions/week?week_start=${weekStart}`);
      setCompleted(new Set(data.filter((d) => d.plan_type === "gym").map((d) => d.day)));
    } catch {}
  };

  useEffect(() => {
    load();
    loadSessions();
    api.get("/match-week/status").then((r) => setMatchStatus(r.data)).catch(() => {});
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/plans/generate", { plan_type: "gym" });
      setPlan(data.content);
      setIsSample(false);
      toast.success("Fresh plan generated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setGenerating(false); }
  };

  const toggleComplete = async (day) => {
    if (completed.has(day)) {
      await api.delete("/sessions/complete", { data: { plan_type: "gym", day, week_start: weekStart } });
      const next = new Set(completed); next.delete(day); setCompleted(next);
    } else {
      await api.post("/sessions/complete", { plan_type: "gym", day, week_start: weekStart });
      const next = new Set(completed); next.add(day); setCompleted(next);
      toast.success(`${day} logged`);
    }
  };

  const toggleMatchWeek = async () => {
    const active = !matchStatus?.manual;
    await api.post("/match-week/toggle", { active });
    const { data } = await api.get("/match-week/status");
    setMatchStatus(data);
    toast.success(active ? "Match week ON" : "Match week OFF");
  };

  const showVideo = async (exerciseName) => {
    try {
      const { data } = await api.get(`/exercises/video`, { params: { name: exerciseName } });
      if (data.video_id) {
        setVideoModal(data);
      } else {
        window.open(data.search_url, "_blank");
      }
    } catch { toast.error("Could not load video"); }
  };

  const isFree = user?.subscription_tier === "free";
  const days = plan?.week || [];
  const completedCount = days.filter((d) => completed.has(d.day)).length;

  return (
    <Layout>
      <div className="p-8 md:p-12">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30]">/ Programming</div>
            <h1 className="font-heading font-black uppercase text-5xl mt-2 flex items-center gap-4">
              <Barbell size={48} weight="duotone" className="text-[#FF3B30]" /> Gym Plan
            </h1>
            {days.length > 0 && (
              <div className="mt-3 text-sm text-[#A3A3A3]">
                Week of {weekStart} · <span className="text-white font-heading">{completedCount}/{days.length}</span> sessions logged
              </div>
            )}
          </div>
          <div className="flex gap-3">
            {!isFree && (
              <Button onClick={toggleMatchWeek} data-testid="toggle-match-week"
                className={`rounded-none font-heading uppercase tracking-widest font-bold h-11 px-4 ${
                  matchStatus?.active ? "bg-[#FF3B30] hover:bg-[#D63026] text-white" : "bg-[#121212] border border-[#262626] text-[#A3A3A3] hover:text-white"
                }`}>
                <Flame size={18} weight={matchStatus?.active ? "fill" : "regular"} className="mr-2" />
                Match Week {matchStatus?.manual ? "On" : matchStatus?.auto_active ? "(Auto)" : "Off"}
              </Button>
            )}
            {!isFree && (
              <Button onClick={generate} disabled={generating || !user?.profile} data-testid="generate-gym"
                className="rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold h-11 px-6">
                <ArrowClockwise size={18} weight="bold" className={`mr-2 ${generating ? "animate-spin" : ""}`} />
                {generating ? "Generating…" : plan && !isSample ? "Regenerate" : "Generate"}
              </Button>
            )}
          </div>
        </div>

        {matchStatus?.active && !isSample && (
          <div className="border-l-4 border-[#FF3B30] bg-[#FF3B30]/10 p-4 mb-6 flex items-center gap-4" data-testid="match-week-notice">
            <Flame size={24} weight="fill" className="text-[#FF3B30]" />
            <div className="text-sm">
              <span className="font-heading font-bold uppercase">Match Week Active.</span>
              <span className="text-[#A3A3A3] ml-2">
                {matchStatus.days_out !== null ? `${matchStatus.days_out} days out. ` : ""}
                Regenerate for a tapered plan (35-45% volume reduction, intensity preserved).
              </span>
            </div>
          </div>
        )}

        {!user?.profile && (
          <div className="border border-[#FF9F0A] bg-[#FF9F0A]/10 p-6 mb-6" data-testid="no-profile-warning">
            Complete your <Link to="/profile" className="text-[#FF3B30] underline font-heading uppercase tracking-widest text-sm">profile</Link> first to generate a tailored plan.
          </div>
        )}

        {isSample && (
          <div className="border border-[#FF3B30] bg-[#FF3B30]/10 p-6 mb-6 flex items-center justify-between" data-testid="sample-notice">
            <div>
              <div className="font-heading uppercase font-bold">Sample Plan</div>
              <div className="text-sm text-[#A3A3A3]">This is a taste. Upgrade to unlock a full 4-day plan tailored to you.</div>
            </div>
            <Link to="/pricing" className="border border-[#FF3B30] bg-[#FF3B30] text-white px-6 py-3 font-heading uppercase tracking-widest text-sm">Upgrade</Link>
          </div>
        )}

        {plan ? (
          <div className="space-y-6">
            {plan.summary && (
              <div className="border border-[#262626] bg-[#121212] p-6">
                <div className="text-xs font-heading uppercase tracking-widest text-[#A3A3A3] mb-2">Rationale</div>
                <p className="text-sm">{plan.summary}</p>
                {plan.focus_areas && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {plan.focus_areas.map((f, i) => (
                      <span key={i} className="border border-[#FF3B30] text-[#FF3B30] px-3 py-1 text-xs uppercase font-heading tracking-widest">{f}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="grid gap-4">
              {days.map((day, i) => {
                const done = completed.has(day.day);
                return (
                  <div key={i} className={`border p-6 ${done ? "border-[#34C759] bg-[#34C759]/5" : "border-[#262626] bg-[#121212]"}`} data-testid={`gym-day-${i}`}>
                    <div className="flex items-center justify-between mb-4 pb-4 border-b border-[#262626]">
                      <div className="flex items-center gap-4">
                        {!isFree && !isSample && (
                          <button onClick={() => toggleComplete(day.day)} data-testid={`complete-${day.day}`}
                            className="hover:scale-110 transition-transform">
                            {done ? <CheckCircle size={32} weight="fill" className="text-[#34C759]" /> : <Circle size={32} className="text-[#A3A3A3]" />}
                          </button>
                        )}
                        <div>
                          <div className={`font-heading font-black text-3xl uppercase ${done ? "line-through text-[#A3A3A3]" : ""}`}>{day.day}</div>
                          <div className="text-xs uppercase tracking-widest text-[#FF3B30] mt-1">{day.focus}</div>
                        </div>
                      </div>
                      {day.duration_minutes && (
                        <div className="font-heading font-bold text-xl text-[#A3A3A3]">{day.duration_minutes} min</div>
                      )}
                    </div>
                    <div className="space-y-3">
                      {day.exercises.map((ex, j) => (
                        <div key={j} className="grid grid-cols-12 gap-2 py-2 border-b border-[#1C1C1C] last:border-0 text-sm items-center">
                          <div className="col-span-12 md:col-span-5 font-semibold flex items-center gap-2">
                            <button onClick={() => showVideo(ex.name)} data-testid={`video-${ex.name.replace(/\s+/g,'-')}`}
                              className="text-[#FF3B30] hover:scale-110 transition-transform" title="Watch technique">
                              <Play size={18} weight="fill" />
                            </button>
                            <span>{ex.name}</span>
                          </div>
                          <div className="col-span-4 md:col-span-2 text-[#A3A3A3]"><span className="text-xs uppercase tracking-widest">Sets</span> <span className="text-white ml-1 font-heading">{ex.sets}</span></div>
                          <div className="col-span-4 md:col-span-2 text-[#A3A3A3]"><span className="text-xs uppercase tracking-widest">Reps</span> <span className="text-white ml-1 font-heading">{ex.reps}</span></div>
                          <div className="col-span-4 md:col-span-3 text-xs text-[#A3A3A3] italic">{ex.notes}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {plan.locked_days && (
              <div className="border-2 border-dashed border-[#262626] p-8 text-center" data-testid="locked-days">
                <Lock size={32} className="text-[#A3A3A3] mx-auto mb-3" />
                <div className="font-heading uppercase tracking-widest text-sm text-[#A3A3A3]">{plan.locked_days} more days locked</div>
                <Link to="/pricing" className="mt-4 inline-block border border-[#FF3B30] bg-[#FF3B30] text-white px-6 py-3 font-heading uppercase tracking-widest text-sm">Unlock Full Plan</Link>
              </div>
            )}

            {plan.progression_notes && (
              <div className="border border-[#262626] bg-[#121212] p-6">
                <div className="text-xs font-heading uppercase tracking-widest text-[#A3A3A3] mb-2">Progression</div>
                <p className="text-sm">{plan.progression_notes}</p>
              </div>
            )}
          </div>
        ) : (
          !isFree && user?.profile && (
            <div className="border-2 border-dashed border-[#262626] p-16 text-center" data-testid="no-plan-yet">
              <Barbell size={48} className="text-[#A3A3A3] mx-auto mb-4" />
              <div className="font-heading uppercase tracking-widest text-[#A3A3A3]">No plan yet</div>
              <div className="text-sm text-[#A3A3A3] mt-2 mb-6">Generate your first AI plan tailored to your position and level.</div>
              <Button onClick={generate} disabled={generating} data-testid="generate-first"
                className="rounded-none bg-[#FF3B30] hover:bg-[#D63026] font-heading uppercase tracking-widest font-bold">
                {generating ? "Generating…" : "Generate My Plan"}
              </Button>
            </div>
          )
        )}
      </div>

      <Dialog open={!!videoModal} onOpenChange={(o) => !o && setVideoModal(null)}>
        <DialogContent className="bg-[#121212] border-[#262626] rounded-none max-w-3xl p-0 max-h-[90vh] overflow-y-auto">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle className="font-heading font-black uppercase text-2xl flex items-center gap-2">
              <Play size={24} weight="fill" className="text-[#FF3B30]" /> {videoModal?.name}
            </DialogTitle>
          </DialogHeader>
          {videoModal?.video_id && (
            <div className="aspect-video w-full">
              <iframe
                data-testid="exercise-video-iframe"
                src={`https://www.youtube.com/embed/${videoModal.video_id}?autoplay=1&modestbranding=1&rel=0`}
                title={videoModal.name}
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          )}
          {videoModal?.progression && (
            <div className="border-t border-[#262626] p-6" data-testid="progression-section">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs font-heading uppercase tracking-widest text-[#FF3B30]">Next Progression</div>
                  <div className="font-heading font-bold uppercase text-xl">{videoModal.progression.name}</div>
                </div>
                <span className="text-xs font-heading uppercase tracking-widest border border-[#FF3B30] text-[#FF3B30] px-2 py-1">Level Up</span>
              </div>
              <div className="aspect-video w-full">
                <iframe
                  data-testid="progression-video-iframe"
                  src={`https://www.youtube.com/embed/${videoModal.progression.video_id}?modestbranding=1&rel=0`}
                  title={videoModal.progression.name}
                  allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </div>
          )}
          <div className="p-4 text-xs text-[#A3A3A3] text-center">
            Video sourced from YouTube. Always warm up before heavy sets.
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
